import { modelStatesAt } from './framing.js';
import { circleSamples } from './model-path.js';
import { modelPosesAt, modelScale, modelSlugOf, modelSweptBox, replayModels } from './models.js';
import { CODE_WORLD_CATALOGUE, worldIn, } from './world-catalogue.js';
import { SIGHT, groundAtSight, sightBin } from './world-package/measure.js';
/**
 * Whether the pupil can see a model where it stands (generator v6, ADR-053): not behind the world's
 * scenery -- rocks, a reef, kelp, buildings, a prop -- and not lost in its fog. Judged from a seated
 * pupil's eyes at the stage centre (SIGHT.eye), on nine points of the model's box, by the world's
 * sight profile (world-package/measure.ts) and fog density. Shared by the spots a prompt offers
 * (model-spots.ts), the generator's check "Niet achter de wereld" and `pnpm lessons:check`.
 */
/** Of the nine points, at least this many must be clear. */
export const SIGHT_CLEAR = 7;
/**
 * A point is hidden only this many degrees or more below the scenery: the profile is coarse (5° bins,
 * whole degrees, the land under it per cell), and a hand's width of ground before a model's feet hides
 * nothing that matters (the island's T. rex at 20 m, 10 Oct 2026). Kept at 2° when the ground under the
 * profile became known (12 Oct 2026).
 */
export const SIGHT_TOLERANCE = 2;
/**
 * A model going round (ADR-054) is judged at every place of its ring that `circleSamples` gives (every
 * 0.25 m of the arc it travels; the whole ring for a loop or a round or more): its ring fails when
 * more than this share of those places are not seen well (more than 9 − SIGHT_CLEAR points hidden).
 * A rock it passes behind for a moment is fine; a quarter of its round out of sight is not.
 */
export const RING_HIDDEN_MAX = 0.25;
/** A model's place must be at least this visible through the fog (three.js FogExp2). */
export const FOG_VISIBLE = 0.6;
/** How much of something `distance` metres away shows through FogExp2 of `density`: 1 without fog. */
export function fogVisibility(density, distance) {
    return density === undefined || density <= 0 ? 1 : Math.exp(-((density * distance) ** 2));
}
/**
 * Whether `point` lies below the scenery nearer than it, seen from the eyes at the stage centre, by
 * SIGHT_TOLERANCE or more.
 */
export function hiddenBySight(profile, point) {
    const [x, y, z] = point;
    const r = Math.hypot(x, z);
    let ring = -1;
    for (let k = 0; k < SIGHT.rings.length; k++)
        if (SIGHT.rings[k] <= r)
            ring = k;
    if (ring < 0)
        return false;
    const elevation = (Math.atan2(y - SIGHT.eye, r) * 180) / Math.PI;
    return elevation < profile[sightBin(x, z)][ring] - SIGHT_TOLERANCE;
}
/**
 * Nine points on a model standing at `pos` ([x, z]) turned `turn`: front, middle and back along its
 * length (at 10, 50 and 90 %), each at 20, 50 and 90 % of its height, on its centre line; its box as
 * the clips sweep it, at the size it is shown, standing on the land (`ground`) and `above` it.
 */
export function modelSightPoints(facts, pos, turn, options = {}) {
    const scale = options.scale ?? 1;
    const above = (options.ground ?? 0) + (options.above ?? 0);
    const box = modelSweptBox(facts, options.clips);
    const x = ((box.min[0] + box.max[0]) / 2) * scale;
    const radians = (turn * Math.PI) / 180;
    const c = Math.cos(radians);
    const s = Math.sin(radians);
    const points = [];
    for (const along of [0.1, 0.5, 0.9]) {
        const z = (box.min[2] + (box.max[2] - box.min[2]) * along) * scale;
        for (const up of [0.2, 0.5, 0.9]) {
            // About y as three.js turns rotation.y, as modelFootprint does.
            points.push([
                pos[0] + x * c + z * s,
                above + box.max[1] * scale * up,
                pos[1] - x * s + z * c,
            ]);
        }
    }
    return points;
}
/**
 * How a model at a place is seen: how many of its nine points the scenery hides, and the fog. It
 * stands on the world's land where it is (`world.ground`; flat at 0 when unknown).
 */
export function modelSight(world, facts, pos, turn, options = {}) {
    const profile = world?.sight;
    const ground = groundAtSight(world?.ground, pos[0], pos[1]);
    const hidden = profile === undefined
        ? 0
        : modelSightPoints(facts, pos, turn, { ...options, ground }).filter((p) => hiddenBySight(profile, p)).length;
    return { hidden, visibility: fogVisibility(world?.fog, Math.hypot(pos[0], pos[1])) };
}
/** Whether `modelSight` says the pupil sees it: at least SIGHT_CLEAR points clear, FOG_VISIBLE through the fog. */
export function seenWell(sight) {
    return sight.hidden <= 9 - SIGHT_CLEAR && sight.visibility >= FOG_VISIBLE - 1e-9;
}
const dutch = (n) => String(Math.round(n * 10) / 10).replace('.', ',');
/** Where a ground-plan point is, as the pupil at the stage centre faces: in quarters round them. */
const SIDES = [
    { nl: 'voor je', en: 'ahead' },
    { nl: 'rechts', en: 'right' },
    { nl: 'achter je', en: 'behind' },
    { nl: 'links', en: 'left' },
];
function sideOf(pos) {
    const azimuth = (Math.atan2(pos[0], -pos[1]) * 180) / Math.PI;
    return ((Math.round(azimuth / 90) % 4) + 4) % 4;
}
/**
 * How much of a ring is not seen well: the places of `circle` the scenery hides (more than
 * 9 − SIGHT_CLEAR points) and those the fog takes (less than FOG_VISIBLE through it; a ring round
 * another centre than the pupil's runs nearer and further), and the sides of the pupil (SIDES' order)
 * that hold at least a quarter of them -- where to look, not every stray place -- or all four when
 * each holds a fifth or more (all round).
 */
function ringSight(world, facts, circle, options) {
    // A whole ring ends where it starts: that place is counted once.
    const whole = circle.rounds === 'loop' || circle.rounds >= 1;
    const samples = whole ? circleSamples(circle).slice(0, -1) : circleSamples(circle);
    const perSide = [0, 0, 0, 0];
    let hidden = 0;
    let behind = 0;
    let fog = 0;
    for (const sample of samples) {
        const sight = modelSight(world, facts, sample.pos, sample.turn, options);
        const isBehind = sight.hidden > 9 - SIGHT_CLEAR;
        const inFog = sight.visibility < FOG_VISIBLE - 1e-9;
        if (isBehind)
            behind++;
        if (inFog)
            fog++;
        if (isBehind || inFog) {
            hidden++;
            perSide[sideOf(sample.pos)]++;
        }
    }
    // Spread all round (each side a fifth or more): every side, which the message says as "rondom".
    const round = hidden > 0 && perSide.every((n) => n * 5 >= hidden);
    const sides = round
        ? [0, 1, 2, 3]
        : [0, 1, 2, 3].filter((k) => perSide[k] > 0 && perSide[k] * 4 >= hidden);
    return { hidden, behind, fog, places: samples.length, sides };
}
/**
 * "Niet achter de wereld" (ADR-053): every library model the pupil can see at the end of a step, in
 * a world whose sight profile or fog is known, keeps SIGHT_CLEAR of its nine points clear of the
 * scenery and stands where the fog lets FOG_VISIBLE through. A model that stands still is named once,
 * at the first step it is seen there. A model going round (ADR-054), in the step it starts and in
 * every later step its loop carries on through, is also judged along its ring: more than
 * RING_HIDDEN_MAX of its places hidden by the scenery or lost in the fog fails it, named once per ring
 * (the fog along it since 14 Oct 2026: a ring round a far point runs further off than where it starts). Nothing for an AR lesson or a
 * world nothing is known of.
 */
export function assessSightLines(lesson, catalogue, worlds = CODE_WORLD_CATALOGUE) {
    if (lesson.mode !== 'vr' || lesson.scene.type !== 'world')
        return [];
    const world = worldIn(worlds, lesson.scene.world);
    if (world === undefined || (world.sight === undefined && world.fog === undefined))
        return [];
    const models = Object.entries(lesson.objects ?? {}).flatMap(([id, object]) => {
        const slug = modelSlugOf(object.kind);
        const facts = slug === undefined ? undefined : catalogue[slug];
        return facts ? [{ id, facts, scale: modelScale(object, facts), above: object.above ?? 0 }] : [];
    });
    if (models.length === 0)
        return [];
    const statesAt = modelStatesAt(lesson, catalogue, undefined, worlds);
    const replay = replayModels(lesson, { catalogue });
    const problems = [];
    const named = new Set();
    lesson.steps.forEach((step, index) => {
        const states = statesAt(index);
        for (const { id, facts, scale, above } of models) {
            const state = states.get(id);
            if (!state?.visible)
                continue;
            const pos = [state.pos[0], state.pos[2]];
            const turn = state.rot[1];
            const move = replay.steps[index]?.get(id);
            const clips = move?.clips ?? [];
            const sight = modelSight(world, facts, pos, turn, { scale, clips, above });
            // Going round in this step, or a loop from an earlier one this step does not name.
            const before = modelPosesAt(replay, index - 1).get(id);
            const circle = move === undefined ? before?.circle : move.circle;
            const ring = circle === undefined
                ? undefined
                : `${id}/ring/${circle.centre.join(',')}/${circle.radius.toFixed(2)}/${circle.direction}/${circle.startAngle.toFixed(1)}/${circle.rounds}`;
            // Each ring is judged once: a loop carried through later steps goes round the same ring.
            if (circle !== undefined &&
                ring !== undefined &&
                (world.sight !== undefined || world.fog !== undefined) &&
                !named.has(ring)) {
                named.add(ring);
                const ringClips = move === undefined ? [before.animation] : clips;
                const seen = ringSight(world, facts, circle, { scale, clips: ringClips, above });
                if (seen.hidden > seen.places * RING_HIDDEN_MAX + 1e-9) {
                    const sides = (lang) => {
                        if (seen.sides.length === 4)
                            return lang === 'nl' ? 'rondom' : 'all round';
                        const words = seen.sides.map((k) => SIDES[k][lang]);
                        const and = lang === 'nl' ? ' en ' : ' and ';
                        return words.length < 2
                            ? words.join('')
                            : `${words.slice(0, -1).join(', ')}${and}${words[words.length - 1]}`;
                    };
                    // The scenery's words as before; the fog's when only the fog takes places, both for both.
                    const why = seen.fog === 0
                        ? {
                            nl: 'gaat voor een deel achter de wereld langs',
                            en: 'passes partly behind the world',
                            count: { nl: 'verborgen', en: 'hidden' },
                        }
                        : seen.behind === 0
                            ? {
                                nl: 'verdwijnt voor een deel in de mist',
                                en: 'partly fades into the fog',
                                count: { nl: 'in de mist', en: 'in the fog' },
                            }
                            : {
                                nl: 'gaat voor een deel achter de wereld langs en in de mist',
                                en: 'passes partly behind the world and into the fog',
                                count: { nl: 'niet goed te zien', en: 'not seen well' },
                            };
                    problems.push({
                        step: step.id,
                        object: id,
                        kind: 'ring',
                        en: `step "${step.id}": "${id}"'s round ${why.en} (${sides('en')}; ${seen.hidden} of ${seen.places} places ${why.count.en}, at most a quarter)`,
                        nl: `stap "${step.id}": het rondje van "${id}" ${why.nl} (${sides('nl')}; ${seen.hidden} van ${seen.places} plekken ${why.count.nl}, hooguit een kwart)`,
                    });
                }
            }
            const azimuth = Math.round((Math.atan2(pos[0], -pos[1]) * 180) / Math.PI);
            const distance = Math.hypot(pos[0], pos[1]);
            const where = `${azimuth}°, ${dutch(distance)} m`;
            const key = (kind) => `${id}/${kind}/${pos[0].toFixed(1)},${pos[1].toFixed(1)}`;
            if (sight.hidden > 9 - SIGHT_CLEAR && !named.has(key('hidden'))) {
                named.add(key('hidden'));
                problems.push({
                    step: step.id,
                    object: id,
                    kind: 'hidden',
                    en: `step "${step.id}": "${id}" stands partly behind the world (${sight.hidden} of 9 points hidden; ${azimuth}°, ${distance.toFixed(1)} m)`,
                    nl: `stap "${step.id}": "${id}" staat voor een deel achter de wereld (${sight.hidden} van 9 punten verborgen; ${where})`,
                });
            }
            if (sight.visibility < FOG_VISIBLE - 1e-9 && !named.has(key('fog'))) {
                named.add(key('fog'));
                const percent = Math.round(sight.visibility * 100);
                problems.push({
                    step: step.id,
                    object: id,
                    kind: 'fog',
                    en: `step "${step.id}": "${id}" is lost in the fog (${distance.toFixed(1)} m; ${percent} % visible, at least ${FOG_VISIBLE * 100} %)`,
                    nl: `stap "${step.id}": "${id}" verdwijnt in de mist (${dutch(distance)} m; ${percent} % zichtbaar, minstens ${FOG_VISIBLE * 100} %)`,
                });
            }
        }
    });
    return problems;
}
//# sourceMappingURL=sight.js.map